import React, { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useI18n } from '~Hooks';
import { useExpressCart } from '../../../Providers/ExpressCartProvider';
import { CartLineEdit } from '../../../types';
import CartLine from './CartLine';
import { CartLine as CartLineShape } from './types';
import { buildCartLines, keepUnchangedLines } from './buildCartLines';
import Suggestions, { SUGGESTION_OUT_MS } from './Suggestions';
import PromoCode from './PromoCode';
import LoyaltyBanner from './LoyaltyBanner';
import LoyaltyHint from './LoyaltyHint';

const Title = styled.h2`
  flex: 0 0 auto;
  margin: 0;
  padding: 24px 30px 6px;
  text-align: center;
  font-size: 27px;
  font-weight: 800;
  letter-spacing: -0.5px;
`;

/** How tall the fade at either edge of the list is. */
const FADE_PX = 44;

/**
 * Holds the list and draws a fade over its top and bottom edge, so it is
 * obvious there is more to scroll. Each fade is a pseudo element whose
 * opacity follows a class toggled from the scroll listener - no render.
 */
const ListFrame = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    height: ${FADE_PX}px;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.25s ease-out;
    z-index: 1;
  }

  &::before {
    top: 0;
    background: linear-gradient(to bottom, var(--eposSurface), transparent);
  }

  &::after {
    bottom: 0;
    background: linear-gradient(to top, var(--eposSurface), transparent);
  }

  &.is-scrolled::before,
  &.has-more::after {
    opacity: 1;
  }
`;

const List = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  /* A little more air over the loyalty banner or first line than under the last. */
  padding: 20px 20px 12px;

  &::-webkit-scrollbar {
    display: none;
  }
`;

/** Only the docked panel ever shows this - the drawer is never open on an empty cart. */
const Empty = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 36px 30px;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: var(--eposMutedText);
`;

/**
 * The cart itself: title, grouped lines, "Anything else?" and the promo code.
 * The total lives on the checkout button in the shell. It fills whatever
 * shell holds it - the portrait CartDrawer or the landscape CartPanel - and
 * is the only place cart lines are built.
 *
 * The lines are built in buildCartLines; a line that did not change keeps its
 * previous object, so only the CartLine that actually moved re-renders.
 */
interface Props {
  /** "Ändra"/"Edit" on a cart line - opens ItemDetail prefilled with its picks. */
  onEditLine: (edit: CartLineEdit) => void;
  /** The line being edited, if any - locks the list and dims the other lines, via classes. */
  editing?: CartLineEdit;
}

const CartContents: FC<Props> = ({ onEditLine, editing }) => {
  const { i18n } = useI18n();
  const expressCart = useExpressCart();

  /** key -> the line object handed out last time, for reference stability. */
  const previousLinesRef = useRef(new Map<string, CartLineShape>());
  /** Parent cart ids seen so far - a line whose units were all unknown is new and slides in. */
  const knownIdsRef = useRef(new Set<string>());

  /**
   * The cart holds one entry per unit, upsells as separate linked entries.
   * Lines group identical units, fold their upsells in and sum the price,
   * with stable references so unchanged CartLines skip rendering.
   */
  const lines = useMemo(() => {
    const fresh = buildCartLines(
      expressCart.packagedItems,
      expressCart.appliedDiscount?.title ?? '',
    );

    const stable = keepUnchangedLines(fresh, previousLinesRef.current);
    previousLinesRef.current = new Map(stable.map((line) => [line.key, line]));

    const known = knownIdsRef.current;
    const result = stable.map((line) => ({
      line,
      appeared: !line.ids.some((id) => known.has(id)),
    }));

    knownIdsRef.current = new Set(stable.flatMap((line) => line.ids));
    return result;
  }, [expressCart.packagedItems, expressCart.appliedDiscount?.title]);

  const hasLines = lines.length > 0;

  /**
   * The last line leaving the cart has already finished its own fold-out by
   * the time it drops out of `lines` - but a suggestion attached to it has
   * not started folding away yet (see Suggestions). Holding the list a
   * moment longer here gives that fold a chance to play instead of the
   * empty message cutting it off.
   */
  const [showList, setShowList] = useState(hasLines);
  useEffect(() => {
    if (hasLines) {
      setShowList(true);
      return undefined;
    }
    const timer = window.setTimeout(() => setShowList(false), SUGGESTION_OUT_MS);
    return () => window.clearTimeout(timer);
  }, [hasLines]);

  const frameRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  /** Toggles the edge fades to match where the list is scrolled. */
  const updateFades = useCallback(() => {
    const frame = frameRef.current;
    const list = listRef.current;
    if (!frame || !list) {
      return;
    }
    frame.classList.toggle('is-scrolled', list.scrollTop > 2);
    frame.classList.toggle('has-more', list.scrollTop + list.clientHeight < list.scrollHeight - 2);
  }, []);

  /** Lines slide in and fold out over time, so the content height settles a moment after a change. */
  useEffect(() => {
    updateFades();
    const timer = window.setTimeout(updateFades, 500);
    return () => window.clearTimeout(timer);
  }, [lines, updateFades]);

  /** The shell can change size (rotation, drawer opening) without any scroll or cart change. */
  useEffect(() => {
    const list = listRef.current;
    if (!list || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(updateFades);
    observer.observe(list);
    return () => observer.disconnect();
  }, [updateFades, showList]);

  /** Edit spotlight as classes, so no line renders. Re-run on line changes since a render resets className. */
  useEffect(() => {
    const list = listRef.current;

    if (!list) {
      return;
    }

    list.classList.toggle('is-locked', !!editing);
    const editedKey = editing
      ? lines.find(({ line }) => line.ids.includes(editing.ids[0]))?.line.key
      : undefined;
    // Matched by attribute, not selector - keys hold commas, pipes and free text.
    list.querySelectorAll('[data-line-key]').forEach((block) => {
      const isEdited = !!editing && block.getAttribute('data-line-key') === editedKey;
      block.classList.toggle('is-editing', isEdited);
      block.classList.toggle('is-dimmed', !!editing && !isEdited);
    });
  }, [editing, lines, showList]);

  return (
    <>
      <Title>{i18n('Express.Cart')}</Title>
      {!showList ? (
        <Empty data-testid="epos-cart-empty">{i18n('Express.Cart.Empty')}</Empty>
      ) : (
        <>
          <ListFrame ref={frameRef}>
            <List ref={listRef} data-epos-cart-list="" onScroll={updateFades}>
              {/* Only exists once a loyalty card was tapped - see LoyaltyBanner. */}
              <LoyaltyBanner />
              {lines.map(({ line, appeared }) => (
                <CartLine key={line.key} line={line} appeared={appeared} onEdit={onEditLine} />
              ))}
              {/* The cross-sell moment: the shopper is reviewing, an add-on is one tap away. */}
              <Suggestions />
            </List>
          </ListFrame>
          {/* Only exists when the company has an active code - see PromoCode. */}
          <PromoCode />
        </>
      )}
      {/* Only exists with Loyalty Pro and until a card was tapped - see LoyaltyHint. */}
      <LoyaltyHint />
    </>
  );
};

export default memo(CartContents);
